/**
 * Bundle & Save: collapsible tiers + collection product modal + quick add.
 */

const SECTION_API_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

class BundleAndSave extends HTMLElement {
  #dialog = null;
  #toggle = null;
  #panel = null;
  #hint = null;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-bundle-dialog]');
    this.#toggle = this.querySelector('[data-bundle-toggle]');
    this.#panel = this.querySelector('[data-bundle-panel]');
    this.#hint = this.querySelector('[data-bundle-modal-hint]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#dialog?.addEventListener('click', this.#onDialogClick);
    this.#dialog?.addEventListener('close', this.#onDialogClose);
    this.#syncHint();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
    this.#dialog?.removeEventListener('close', this.#onDialogClose);
  }

  #onClick = (event) => {
    const toggle = event.target.closest('[data-bundle-toggle]');
    if (toggle && this.contains(toggle)) {
      this.#togglePanel();
      return;
    }

    if (event.target.closest('[data-bundle-open-modal]')) {
      this.#openModal();
      return;
    }

    if (event.target.closest('[data-bundle-close-modal]')) {
      this.#closeModal();
      return;
    }

    const addButton = event.target.closest('[data-bundle-add]');
    if (addButton && this.contains(addButton)) {
      this.#addFromCard(addButton);
    }
  };

  #onChange = (event) => {
    if (event.target.matches('[data-bundle-tier]')) {
      this.#syncHint();
      return;
    }

    const select = event.target.closest('[data-bundle-variant]');
    if (!select || select.tagName !== 'SELECT') return;

    const card = select.closest('[data-bundle-card]');
    if (!card) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    this.#updateCard(card, {
      available: option.dataset.available === 'true',
      price: option.dataset.price || '',
      compareAtPrice: option.dataset.comparePrice || '',
      image: option.dataset.image || '',
      url: option.dataset.url || '',
    });
  };

  #onDialogClick = (event) => {
    if (event.target === this.#dialog) {
      this.#closeModal();
    }
  };

  #onDialogClose = () => {
    this.querySelector('[data-bundle-open-modal]')?.focus();
  };

  #togglePanel() {
    if (!this.#toggle || !this.#panel) return;
    const willOpen = this.#toggle.getAttribute('aria-expanded') !== 'true';
    this.#toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    this.#panel.hidden = !willOpen;
  }

  #selectedTier() {
    return this.querySelector('[data-bundle-tier]:checked');
  }

  #syncHint() {
    if (!this.#hint) return;
    const tier = this.#selectedTier();
    if (!tier) {
      this.#hint.textContent = 'Select a bundle tier, then add products.';
      return;
    }

    const qty = tier.dataset.qty || '1';
    const percent = tier.dataset.percent || '0';
    this.#hint.textContent = `Add ${qty}+ twinning set${Number(qty) === 1 ? '' : 's'} to unlock ${percent}% off. Discount auto-applies at checkout.`;
  }

  #openModal() {
    if (!this.#dialog) return;
    this.#syncHint();
    if (typeof this.#dialog.showModal === 'function') {
      this.#dialog.showModal();
    } else {
      this.#dialog.setAttribute('open', '');
    }
  }

  #closeModal() {
    if (!this.#dialog) return;
    if (typeof this.#dialog.close === 'function') {
      this.#dialog.close();
    } else {
      this.#dialog.removeAttribute('open');
    }
  }

  #updateCard(card, { available, price, compareAtPrice, image, url }) {
    const priceEl = card.querySelector('[data-bundle-price]');
    if (priceEl && price) {
      priceEl.innerHTML = compareAtPrice
        ? `<span>${price}</span><s class="bundle-card__compare">${compareAtPrice}</s>`
        : `<span>${price}</span>`;
    }

    const imageEl = card.querySelector('.bundle-card__image');
    if (imageEl && image && imageEl.tagName === 'IMG') {
      imageEl.src = image;
      imageEl.removeAttribute('srcset');
    }

    if (url) {
      card.querySelectorAll('a[href]').forEach((link) => {
        link.href = url;
      });
    }

    const addButton = card.querySelector('[data-bundle-add]');
    if (addButton) addButton.disabled = !available;
  }

  async #addFromCard(button) {
    if (button.disabled) return;

    const card = button.closest('[data-bundle-card]');
    if (!card) return;

    const variantInput = card.querySelector('[data-bundle-variant]');
    const variantId = Number(variantInput?.value);
    if (!variantId) return;

    const label = button.querySelector('[data-bundle-add-label]');
    const busy = button.querySelector('[data-bundle-add-busy]');

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.hidden = true;
    if (busy) busy.hidden = false;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: SECTION_API_HEADERS,
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#cartSections(),
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.description || data?.message || 'Error adding to cart');
      }

      this.#publishCartUpdate(data);
      this.#flashAdded(button, label, busy);
    } catch (error) {
      console.error('[bundle-and-save]', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.hidden = false;
      if (busy) busy.hidden = true;
    }
  }

  #cartSections() {
    const sections = [];
    document.querySelectorAll('cart-items-component[data-section-id], [data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id && !sections.includes(id)) sections.push(id);
    });
    return sections.slice(0, 5);
  }

  #publishCartUpdate(data) {
    const detail = {
      source: 'bundle-and-save',
      data,
      cartData: data,
      sections: data?.sections,
    };

    document.dispatchEvent(new CustomEvent('cart:update', { detail, bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail, bubbles: true }));

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
    }

    this.#openCartDrawer();
  }

  #openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    const dialog = drawer.querySelector('dialog[ref="dialog"], dialog.cart-drawer__dialog');
    if (dialog?.open) return;

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    const trigger =
      drawer.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('[data-testid="cart-drawer-trigger"]');
    trigger?.click();
  }

  #flashAdded(button, label, busy) {
    if (busy) busy.hidden = true;
    if (label) {
      label.hidden = false;
      const original = label.textContent;
      label.textContent = label.dataset.addedLabel || 'Added';
      window.setTimeout(() => {
        label.textContent = original;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1200);
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('bundle-and-save')) {
  customElements.define('bundle-and-save', BundleAndSave);
}